<script setup>
import DeleteTaskAction from "@frontJs/components/actions/projectManagement/tasks/DeleteTaskAction.vue";
import ApikomTaskTable from "@frontJs/components/tables/projectManagement/tasks/ApikomTaskTable.vue";
import ApikomTaskTimeTable from "@frontJs/components/tables/projectManagement/tasks/ApikomTaskTimeTable.vue";
import { router, usePage } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import ComboboxField from "@js/components/fields/ComboboxField.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import SwitchField from "@js/components/fields/SwitchField.vue";
import TimeField from "@js/components/fields/TimeField.vue";
import WysiwygField from "@js/components/fields/WysiwygField.vue";
import SpinnerIcon from "@js/components/icons/SpinnerIcon.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { goBack, useRoute } from "@js/utils/route";
import { useLocalStorage } from "@vueuse/core";
import { useUrlSearchParams } from "@vueuse/core";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import { Form } from "vee-validate";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { object, string, array } from "yup";

const { t } = useI18n();

const types = [
  { value: "hourly", label: t("label.hourly") },
  { value: "package", label: t("label.package") },
  { value: "project", label: t("label.project") },
];

const recurrentTypes = [
  { value: "daily", label: t("label.daily") },
  { value: "weekly", label: t("label.weekly") },
  { value: "monthly", label: t("label.monthly") },
  { value: "twice_yearly", label: t("label.twiceYearly") },
  { value: "yearly", label: t("label.yearly") },
];

const themes = [
  { value: "seo", label: t("label.seo") },
  { value: "sea", label: t("label.sea") },
  { value: "smo", label: t("label.smo") },
  { value: "sma", label: t("label.sma") },
  { value: "sem", label: t("label.sem") },
  { value: "dev", label: t("label.dev") },
  { value: "dev_seo", label: t("label.devSeo") },
  { value: "cs", label: t("label.cs") },
];

const statuses = [
  {
    value: "open",
    label: t("label.open"),
    default: true,
    recurrent: false,
  },
  {
    value: "closed",
    label: t("label.closed"),
    default: true,
    recurrent: true,
  },
  {
    value: "to_process",
    label: t("label.toProcess"),
    default: false,
    recurrent: true,
  },
];

const priorities = [
  { value: "low", label: t("label.low") },
  { value: "medium", label: t("label.medium") },
  { value: "high", label: t("label.high") },
];

const {
  task,
  tenants,
  users,
  idea = false,
} = defineProps({
  task: {
    required: false,
    type: Object,
  },
  tenants: {
    required: true,
    type: Array,
  },
  users: {
    required: true,
    type: Array,
  },
  idea: {
    required: false,
    type: Boolean,
  },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const formRef = ref(null);

const selectedTab = ref(0);

const currentUser = computed(() => usePage().props.currentUser);

const params = useUrlSearchParams("history");

const form = reactive({
  name: params.name ? params.name : task ? task.name : "",
  description: task ? task.description : "",
  internalComment: task ? task.internalComment : "",
  type: params.type
    ? types.find((type) => type.value === params.type)
    : task
      ? types.find((type) => type.value === task.type)
      : types[0],
  recurrentType:
    task && task.recurrentType
      ? recurrentTypes.find(
          (recurrentType) => recurrentType.value === task.recurrentType,
        )
      : null,
  theme: params.theme
    ? themes.find((theme) => theme.value === params.theme)
    : task
      ? themes.find((theme) => theme.value === task.theme)
      : useLocalStorage(
          "default-apikom-task-theme",
          currentUser.value.groups && currentUser.value.groups.length > 0
            ? themes.find(
                (theme) => theme.value === currentUser.value.groups[0].key,
              )
            : themes[0],
        ),
  status: task
    ? statuses.find((status) => status.value === task.status)
    : statuses[0],
  priority: task
    ? priorities.find((priority) => priority.value === task.priority)
    : priorities[1],
  deadline: task ? task.deadline : new Date().toISOString().split("T")[0],
  tenants: [],
  tenant: task ? tenants.find((tenant) => tenant.id === task.tenantId) : null,
  principalUsers: params.noOneResponsible
    ? []
    : task
      ? users.filter((user) => task.principalUserIds.includes(user.id))
      : users.filter((user) => user.id === currentUser.value.id),
  noPrincipalUsers: params.noOneResponsible
    ? []
    : task
      ? users.filter((user) => task.noPrincipalUserIds.includes(user.id))
      : [],
  noOneResponsible: params.noOneResponsible
    ? true
    : task
      ? task.userIds.length === 0
      : false,
  taskTime: {
    date: new Date().toISOString().split("T")[0],
    hours: "",
    minutes: "",
    user: users.find((user) => user.id === currentUser.value.id) ?? users[0],
  },
});

const formattedPrincipalUsers = computed(() => {
  return users.filter((user) => {
    for (let i = 0; i < form.noPrincipalUsers.length; i++) {
      if (form.noPrincipalUsers[i].id === user.id) {
        return false;
      }
    }

    return true;
  });
});

const formattedNoPrincipalUsers = computed(() => {
  return users.filter((user) => {
    for (let i = 0; i < form.principalUsers.length; i++) {
      if (form.principalUsers[i].id === user.id) {
        return false;
      }
    }

    return true;
  });
});

const formattedSelectedUsers = computed(() => {
  return users.filter((user) => {
    for (let i = 0; i < form.principalUsers.length; i++) {
      if (form.principalUsers[i].id === user.id) {
        return true;
      }
    }

    for (let i = 0; i < form.noPrincipalUsers.length; i++) {
      if (form.noPrincipalUsers[i].id === user.id) {
        return true;
      }
    }

    return false;
  });
});

const formattedNamePrefix = computed(() => {
  let namePrefix = task
    ? `[${form.tenant.name}]`
    : `[${form.tenants.map((tenant) => tenant.name).join(", ")}]`;
  namePrefix += ` [${t("abbreviation." + form.type.value)}]`;
  namePrefix += ` [${t("label." + useChangeCase(form.theme.value, "camelCase").value)}]`;

  if (form.recurrentType) {
    namePrefix += ` [${t("abbreviation.recurrent")}]`;

    if (form.recurrentType.value === "weekly") {
      namePrefix += ` [${t("abbreviation.onceAWeek")}]`;
    }

    if (form.recurrentType.value === "monthly") {
      namePrefix += ` [${t("abbreviation.onceAMonth")}]`;
    }

    if (form.recurrentType.value === "twice_yearly") {
      namePrefix += ` [${t("abbreviation.twiceAYear")}]`;
    }

    if (form.recurrentType.value === "yearly") {
      namePrefix += ` [${t("abbreviation.onceAYear")}]`;
    }
  }

  namePrefix += ` -`;

  return namePrefix;
});

const formattedStatuses = computed(() => {
  return statuses.filter((status) => {
    if (form.recurrentType) {
      return status.recurrent;
    } else {
      return status.default;
    }
  });
});

const tabs = computed(() => {
  const tabs = {
    information: t("title.information"),
  };

  if (!form.noOneResponsible) {
    if (!idea || (task && !task.isIdea)) {
      tabs.time = t("title.time");
    }
  }

  if (task && !task.isChild) {
    tabs.subTasks = t("title.subTasks");
  }

  return tabs;
});

const validationSchema = object({
  name: string().required(t("error.required")),
  deadline: string().required(t("error.required")),
  type: object().required(t("error.required")),
  recurrentType: object().nullable(),
  theme: object().required(t("error.required")),
  status: object().required(t("error.required")),
  priority: object().required(t("error.required")),
  tenants: array().min(1, t("error.required")),
  principalUsers: array().when("noOneResponsible", {
    is: false,
    then: () => array().min(1, t("error.required")),
  }),
  noPrincipalUsers: array().when("noOneResponsible", {
    is: false,
    then: () => array().min(1, t("error.required")),
  }),
});

function changeTab(index) {
  selectedTab.value = index;
}

function saveTask() {
  isSubmitting.value = true;

  let url = task
    ? route("front.projectManagement.task.postTask", {
        task: task.id,
      })
    : route("front.projectManagement.task.postTask");

  let postData = {
    name: form.name,
    description: form.description,
    internalComment: form.internalComment,
    type: form.type.value,
    recurrentType: form.recurrentType?.value,
    theme: form.theme.value,
    status: form.status.value,
    priority: form.priority.value,
    deadline: form.deadline,
    principalUserIds: form.principalUsers.map((user) => user.id),
    noPrincipalUserIds: form.noPrincipalUsers.map((user) => user.id),
    taskTime: {
      date: form.taskTime.date,
      hours: form.taskTime.hours ?? "00",
      minutes: form.taskTime.minutes,
      userId: form.taskTime.user.id,
    },
    isIdea: task ? task.isIdea : idea,
  };

  if (task) {
    postData = {
      ...postData,
      tenantId: form.tenant.id,
    };
  } else {
    postData = {
      ...postData,
      tenantIds: form.tenants.map((tenant) => tenant.id),
    };
  }

  let error;

  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content"),
    },
    body: JSON.stringify(postData),
  })
    .then((response) => {
      error = !response.ok;

      return response.json();
    })
    .then((data) => {
      if (error) {
        toast.error(data.message, { position: "top-right" });
      } else {
        toast.success(t("toast.successfullySaved"), {
          position: "top-right",
        });

        goBack();
      }
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
      router.visit(route("front.projectManagement.task.tasks"));
    });
}

function deleteCallback() {
  toast.success(t("toast.successfullyDeleted"), { position: "top-right" });
  router.visit(route("front.projectManagement.task.tasks"), {
    preserveState: true,
    replace: true,
  });
}

watch(form, () => {
  if (form.recurrentType && form.status.value === "open") {
    form.status = statuses.find((status) => status.value === "to_process");
  }

  if (form.recurrentType === null && form.status.value === "to_process") {
    form.status = statuses.find((status) => status.value === "open");
  }

  if (form.recurrentType === null && form.status.value === "processed") {
    form.status = statuses.find((status) => status.value === "closed");
  }
});

watch(
  () => form.noOneResponsible,
  () => {
    if (form.noOneResponsible) {
      form.principalUsers = [];
      form.noPrincipalUsers = [];
    }
  },
);

onMounted(() => {
  router.on("before", (event) => {
    const url = new URL(window.location.href);
    const newUrl = event.detail.visit.url;

    if (
      !isSubmitting.value &&
      formRef.value &&
      formRef.value.getMeta().touched &&
      url.pathname !== newUrl.pathname
    ) {
      return confirm(t("text.confirmLeaveWithoutSaving"));
    }
  });
});
</script>

<template>
  <Form
    ref="formRef"
    :validation-schema="validationSchema"
    @submit="saveTask"
    v-slot="{ errors }"
  >
    <TabGroup :selected-tab="selectedTab" :change-tab="changeTab" :tabs="tabs" :replace="false">
      <template #panels>
        <TabPanel :unmount="false">
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <ListboxField
              v-model="form.type"
              field-id="type"
              field-name="type"
              :label="t('label.type')"
              :options="types"
              required
              :disabled="task && task.isChild"
            >
              <template #buttonContent>
                {{ form.type.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.recurrentType"
              field-id="recurrentType"
              field-name="recurrentType"
              :label="t('label.recurrent')"
              :options="recurrentTypes"
              has-empty-option
              :disabled="task && task.isChild"
            >
              <template #buttonContent>
                {{ form.recurrentType.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.theme"
              field-id="theme"
              field-name="theme"
              :label="t('label.theme')"
              :options="themes"
              required
              :disabled="task && task.isChild"
            >
              <template #buttonContent>
                {{ form.theme.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <div class="grid grid-cols-1 gap-x-8 lg:grid-cols-4">
            <ComboboxField
              v-if="task"
              v-model="form.tenant"
              :options="tenants"
              :label="t('label.customer')"
              field-name="tenant"
              field-id="tenant"
              filteredOptionKey="name"
              required
              :disabled="task && task.isChild"
            >
              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ComboboxField>

            <ComboboxField
              v-else
              v-model="form.tenants"
              :options="tenants"
              :label="t('label.customers')"
              field-name="tenants"
              field-id="tenants"
              filteredOptionKey="name"
              multiple
              required
            >
              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ComboboxField>
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <InputField
            v-model="form.name"
            :label="t('label.name')"
            :placeholder="t('label.name')"
            field-name="name"
            field-id="name"
            type="text"
            :max="138 - formattedNamePrefix.length"
            required
          >
            <template #inputPrefix>
              {{ formattedNamePrefix }}
            </template>
          </InputField>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <SwitchField
            v-model="form.noOneResponsible"
            :label="t('label.noOneResponsible')"
            field-name="noOneResponsible"
            field-id="noOneResponsible"
          />

          <div class="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
            <ComboboxField
              v-model="form.principalUsers"
              :options="formattedPrincipalUsers"
              :label="t('label.principalTaskManagers')"
              field-name="principalUsers"
              field-id="principalUsers"
              filteredOptionKey="fullName"
              multiple
              required
              :disabled="(task && task.isChild) || form.noOneResponsible"
            >
              <template #optionContent="{ option }">
                {{ option.fullName }}
              </template>
            </ComboboxField>

            <ComboboxField
              v-model="form.noPrincipalUsers"
              :options="formattedNoPrincipalUsers"
              :label="t('label.noPrincipalTaskManagers')"
              field-name="noPrincipalTaskManagers"
              field-id="noPrincipalTaskManagers"
              filteredOptionKey="fullName"
              multiple
              :disabled="(task && task.isChild) || form.noOneResponsible"
            >
              <template #optionContent="{ option }">
                {{ option.fullName }}
              </template>
            </ComboboxField>
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.deadline"
              :label="t('label.deadline')"
              :placeholder="t('label.deadline')"
              field-name="deadline"
              field-id="deadline"
              type="date"
              required
              :disabled="task && task.isChild"
            />

            <ListboxField
              v-model="form.priority"
              field-id="priority"
              field-name="priority"
              :label="t('label.priority')"
              :options="priorities"
              required
              :disabled="task && task.isChild"
            >
              <template #buttonContent>
                {{ form.priority.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.status"
              field-id="status"
              field-name="status"
              :label="t('label.status')"
              :options="formattedStatuses"
              :disabled="task && task.isChild"
              required
            >
              <template #buttonContent>
                {{ form.status.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <WysiwygField
            v-model="form.description"
            :label="t('label.description')"
            field-name="description"
            field-id="description"
          />

          <WysiwygField
            v-model="form.internalComment"
            :label="t('label.internalComment')"
            field-name="internalComment"
            field-id="internalComment"
          />
        </TabPanel>

        <TabPanel
          v-if="!idea || (task && !task.isIdea)"
          :without-background="typeof task === 'object'"
          :unmount="typeof task === 'object'"
        >
          <template v-if="typeof task === 'undefined' || task === null">
            <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
              <InputField
                v-model="form.taskTime.date"
                :label="t('label.date')"
                :placeholder="t('label.date')"
                field-name="date"
                field-id="date"
                type="date"
              />
            </div>

            <hr class="h-0.25 my-4 w-full bg-gray-200" />

            <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
              <ListboxField
                v-model="form.taskTime.user"
                field-id="user"
                field-name="user"
                :label="t('label.taskManager')"
                :options="formattedSelectedUsers"
              >
                <template #buttonContent>
                  {{ form.taskTime.user.fullName }}
                </template>

                <template #optionContent="{ option }">
                  {{ option.fullName }}
                </template>
              </ListboxField>
            </div>

            <hr class="h-0.25 my-4 w-full bg-gray-200" />

            <TimeField
              v-model:hours="form.taskTime.hours"
              v-model:minutes="form.taskTime.minutes"
              hours-field-id="hours"
              hours-field-name="hours"
              :minutes="form.minutes"
              minutes-field-id="minutes"
              minutes-field-name="minutes"
              :label="t('label.time')"
            />
          </template>

          <ApikomTaskTimeTable v-else :task="task" without-filters />
        </TabPanel>

        <TabPanel
          v-if="task && !task.isChild"
          without-background
          :unmount="true"
        >
          <ApikomTaskTable :task="task" />
        </TabPanel>
      </template>
    </TabGroup>

    <div
      v-if="
        ![1, 2].includes(selectedTab) ||
        typeof task === 'undefined' ||
        task === null
      "
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton type="transparent" :callback="() => goBack()">
        {{ t("button.back") }}
      </ActionButton>

      <DeleteTaskAction v-if="task" :task="task" :callback="deleteCallback">
        <template #button="{ setIsOpen }">
          <ActionButton type="secondary" :callback="() => setIsOpen(true)">
            {{ t("button.delete") }}
          </ActionButton>
        </template>
      </DeleteTaskAction>

      <ActionButton
        v-if="
          !task &&
          form.taskTime.hours === '' &&
          form.taskTime.minutes === '' &&
          selectedTab === 0 &&
          !form.noOneResponsible &&
          !idea
        "
        :callback="() => changeTab(1)"
      >
        {{ t("button.save") }}
      </ActionButton>

      <ActionButton
        v-else
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.save") }}
        </template>

        <template v-else>
          <SpinnerIcon
            class="mr-1 size-4 animate-spin fill-white text-gray-200"
          />
          {{ t("button.saveInProgress") }}
        </template>
      </ActionButton>

      <div v-if="Object.keys(errors).length > 0" class="text-sm text-red-500">
        {{ t("error.formHasErrors") }}
      </div>
    </div>
  </Form>
</template>
